import { useState, useRef, useEffect } from "react";
import JSZip from "jszip";
import { convertBatch } from "../../api/client";
import type { ConversionConfig, BatchConvertResponse, RuleResult } from "../../types/sigwaz";
import { LEVEL_MAP } from "../../types/sigwaz";
import { DottedSurface } from "../ui/DottedSurface";
import { UploadIcon, SpinnerIcon, RefreshIcon, DownloadIcon, XIcon } from "../ui/Icons";

const MAX_YAML_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_ZIP_SIZE  = 50 * 1024 * 1024; // 50 MB

interface Props {
  config: ConversionConfig;
  convertTrigger: number;
  onReadyChange: (ready: boolean) => void;
}

interface ImportMeta {
  sourceName: string;
  isFlat: boolean;
  subResults?: Map<string, BatchConvertResponse>;
}

function dlBlob(content: string | Blob, filename: string, mimeType = "application/xml") {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function baseName(filename: string): string {
  return filename.replace(/\.(yml|yaml|zip)$/i, "");
}

function RuleRow({ rule }: { rule: RuleResult }) {
  const hasErr   = rule.errors.length > 0;
  const hasWrn   = rule.skipped.length > 0;
  const mitre    = rule.mitre_techniques.slice(0, 3);
  const levelInt = LEVEL_MAP[rule.sigma_level] ?? 10;

  return (
    <div className={`rule-row${hasErr ? " r-err" : hasWrn ? " r-warn" : ""}`}>
      <span className={`rs ${hasErr ? "rs-err" : hasWrn ? "rs-wrn" : "rs-ok"}`}>
        {hasErr ? "✕" : hasWrn ? "⚠" : "✓"}
      </span>
      <div className="ri">
        <span className="ri-name">{rule.sigma_title || "Unknown Rule"}</span>
        <span className="ri-meta">
          ID {rule.rule_ids[0] ?? "-"} · {rule.sigma_level} → level {levelInt}
          {mitre.length > 0 && ` · ${mitre.join(", ")}`}
          {rule.rule_ids.length > 1 && ` (+${rule.rule_ids.length - 1} rules)`}
        </span>
        {hasErr && rule.errors.map((e, i) => (
          <span key={i} className="ri-err">⚠ {e}</span>
        ))}
        {hasWrn && rule.skipped.map((s, i) => (
          <span key={i} className="ri-err" style={{ color: "var(--warning, #f59e0b)" }}>
            ⚠ Skipped ({s.reason}){s.detail ? `: ${s.detail}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

export function BatchImporter({ config, convertTrigger, onReadyChange }: Props) {
  const [dragging, setDragging]       = useState(false);
  const [processing, setProcessing]   = useState(false);
  const [progress, setProgress]       = useState("");
  const [batchResult, setBatchResult] = useState<BatchConvertResponse | null>(null);
  const [importMeta, setImportMeta]   = useState<ImportMeta | null>(null);
  const [error, setError]             = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingType, setPendingType] = useState<"yaml" | "zip" | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  // Notify parent when unmounted so it can reset canConvert
  useEffect(() => {
    return () => { onReadyChange(false); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Run conversion when parent increments convertTrigger
  useEffect(() => {
    if (convertTrigger === 0 || !pendingFile || !pendingType) return;
    if (pendingType === "zip") processZip(pendingFile);
    else processYaml(pendingFile);
  }, [convertTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── YAML file ─────────────────────────────────────────────────────────────
  const processYaml = async (file: File) => {
    setProcessing(true);
    setError(null);
    setImportMeta(null);
    try {
      const text = await file.text();
      const res  = await convertBatch(text, config);
      setBatchResult(res);
      setImportMeta({ sourceName: baseName(file.name), isFlat: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setProcessing(false);
      setProgress("");
    }
  };

  // ── ZIP archive ───────────────────────────────────────────────────────────
  const processZip = async (file: File) => {
    setProcessing(true);
    setError(null);
    setBatchResult(null);
    setImportMeta(null);

    const archiveName = baseName(file.name);

    try {
      const zip = await JSZip.loadAsync(file);

      const entries: { path: string; content: string }[] = [];
      for (const [path, entry] of Object.entries(zip.files)) {
        if (!entry.dir && /\.(yml|yaml)$/i.test(path)) {
          const content = await entry.async("string");
          entries.push({ path, content });
        }
      }

      if (!entries.length) {
        setError("No YAML files found in the archive.");
        return;
      }

      const allPaths   = entries.map(e => e.path);
      const firstParts = allPaths.map(p => p.split("/")[0]);
      const commonRoot =
        firstParts.every(p => p === firstParts[0]) && allPaths.some(p => p.includes("/"))
          ? firstParts[0]
          : null;

      const normalized = entries.map(e => ({
        content: e.content,
        path: commonRoot ? e.path.slice(commonRoot.length + 1) : e.path,
      }));

      const rootContents: string[]             = [];
      const subfolderMap = new Map<string, string[]>();

      for (const { path, content } of normalized) {
        const parts = path.split("/").filter(Boolean);
        if (parts.length <= 1) {
          rootContents.push(content);
        } else {
          const sub = parts[0];
          const g   = subfolderMap.get(sub) ?? [];
          g.push(content);
          subfolderMap.set(sub, g);
        }
      }

      const hasSubfolders = subfolderMap.size > 0;

      if (!hasSubfolders) {
        setProgress("Converting…");
        const combined = rootContents.join("\n---\n");
        const res      = await convertBatch(combined, config);
        setBatchResult(res);
        setImportMeta({ sourceName: archiveName, isFlat: true });
      } else {
        const subResults    = new Map<string, BatchConvertResponse>();
        const subfolderKeys = Array.from(subfolderMap.keys());

        for (let i = 0; i < subfolderKeys.length; i++) {
          const sub      = subfolderKeys[i];
          setProgress(`Processing ${i + 1}/${subfolderKeys.length}: ${sub}`);
          const contents = subfolderMap.get(sub)!;
          const combined = contents.join("\n---\n");
          const res      = await convertBatch(combined, config);
          subResults.set(sub, res);
        }

        if (rootContents.length > 0) {
          setProgress("Processing root files…");
          const combined = rootContents.join("\n---\n");
          const res      = await convertBatch(combined, config);
          subResults.set("_root", res);
        }

        setImportMeta({ sourceName: archiveName, isFlat: false, subResults });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setProcessing(false);
      setProgress("");
    }
  };

  const handleFile = (file: File) => {
    const isZip  = /\.zip$/i.test(file.name);
    const isYaml = /\.(yml|yaml)$/i.test(file.name);

    if (!isZip && !isYaml) {
      setError("Only .yml, .yaml, or .zip files are supported.");
      return;
    }
    if (isZip && file.size > MAX_ZIP_SIZE) {
      setError(`ZIP too large (max 50 MB). This file is ${(file.size / 1048576).toFixed(1)} MB.`);
      return;
    }
    if (isYaml && file.size > MAX_YAML_SIZE) {
      setError(`YAML too large (max 10 MB). This file is ${(file.size / 1048576).toFixed(1)} MB.`);
      return;
    }

    setError(null);
    setBatchResult(null);
    setImportMeta(null);
    setPendingFile(file);
    setPendingType(isZip ? "zip" : "yaml");
    onReadyChange(true);
  };

  const reset = () => {
    setBatchResult(null);
    setImportMeta(null);
    setError(null);
    setProgress("");
    setPendingFile(null);
    setPendingType(null);
    onReadyChange(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  async function addResultToZip(zip: JSZip, stem: string, res: BatchConvertResponse) {
    const chunks = res.xml_files.length > 1 ? res.xml_files : [res.merged_xml].filter(Boolean);
    if (chunks.length === 1) {
      zip.file(`${stem}_rules.xml`, chunks[0]);
    } else {
      chunks.forEach((xml, i) => zip.file(`${stem}_rules-${i + 1}.xml`, xml));
    }
  }

  const downloadResult = async () => {
    if (!importMeta) return;

    if (!importMeta.isFlat && importMeta.subResults) {
      const zip = new JSZip();
      for (const [sub, res] of importMeta.subResults.entries()) {
        await addResultToZip(zip, sub, res);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      dlBlob(blob, `${importMeta.sourceName}_rules.zip`, "application/zip");
      return;
    }

    if (!batchResult) return;

    const isSplit = batchResult.xml_files.length > 1;
    if (isSplit) {
      const zip = new JSZip();
      batchResult.xml_files.forEach((xml, i) =>
        zip.file(`${importMeta.sourceName}_rules-${i + 1}.xml`, xml)
      );
      const blob = await zip.generateAsync({ type: "blob" });
      dlBlob(blob, `${importMeta.sourceName}_rules.zip`, "application/zip");
    } else {
      dlBlob(batchResult.merged_xml, `${importMeta.sourceName}_rules.xml`);
    }
  };

  // ── Flat results view ─────────────────────────────────────────────────────
  if (batchResult && importMeta?.isFlat) {
    const ok     = batchResult.results.filter(r => r.rule_count > 0 && !r.errors.length).length;
    const err    = batchResult.total_errors;
    const warn   = batchResult.total_skipped;
    const isSplit = batchResult.xml_files.length > 1;
    const dlName  = isSplit
      ? `${importMeta.sourceName}_rules.zip`
      : `${importMeta.sourceName}_rules.xml`;
    const kbSize = isSplit
      ? (batchResult.xml_files.reduce((s, x) => s + x.length, 0) / 1024).toFixed(1)
      : (batchResult.merged_xml.length / 1024).toFixed(1);

    return (
      <div className="batch-area">
        <div className="batch-results fade-in">
          <div className="batch-stats">
            <span className="stat-pill sp-ok">✓ {ok} converted</span>
            {err  > 0 && <span className="stat-pill sp-err">✕ {err} error{err > 1 ? "s" : ""}</span>}
            {warn > 0 && <span className="stat-pill sp-warn">⚠ {warn} skipped</span>}
            {isSplit && (
              <span className="stat-pill sp-info">⊞ {batchResult.xml_files.length} files</span>
            )}
            <span className="stat-pill sp-info">
              {batchResult.mitre_techniques.length > 0
                ? batchResult.mitre_techniques.slice(0, 4).join(", ")
                : "No MITRE tags"}
            </span>
            <button className="btn-ghost" style={{ marginLeft: "auto" }} onClick={reset}>
              <RefreshIcon size={12} /> New import
            </button>
          </div>

          <div className="rule-list">
            {batchResult.results.map((r, i) => <RuleRow key={i} rule={r} />)}
          </div>

          <button className="btn-dl" onClick={downloadResult}>
            <DownloadIcon size={15} /> {dlName}{" "}
            <span style={{ opacity: 0.7, fontSize: 12, fontWeight: 400 }}>
              {batchResult.total_rules} rule{batchResult.total_rules !== 1 ? "s" : ""}
              {isSplit && ` · ${batchResult.xml_files.length} files`}
              {" "}· {kbSize} KB
            </span>
          </button>

          {!batchResult.valid && batchResult.validation_errors.length > 0 && (
            <div style={{ color: "#ef4444", fontSize: 11, marginTop: 8 }}>
              ⚠ XML validation: {batchResult.validation_errors.join(" · ")}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Structured ZIP results view ───────────────────────────────────────────
  if (importMeta && !importMeta.isFlat && importMeta.subResults) {
    const subResults = importMeta.subResults;
    let totalRules = 0, totalErrors = 0, totalSkipped = 0;
    for (const res of subResults.values()) {
      totalRules   += res.total_rules;
      totalErrors  += res.total_errors;
      totalSkipped += res.total_skipped;
    }
    const dlName = `${importMeta.sourceName}_rules.zip`;

    return (
      <div className="batch-area">
        <div className="batch-results fade-in">
          <div className="batch-stats">
            <span className="stat-pill sp-ok">✓ {totalRules} rules</span>
            {totalErrors  > 0 && <span className="stat-pill sp-err">✕ {totalErrors} error{totalErrors > 1 ? "s" : ""}</span>}
            {totalSkipped > 0 && <span className="stat-pill sp-warn">⚠ {totalSkipped} skipped</span>}
            <span className="stat-pill sp-info">{subResults.size} group{subResults.size !== 1 ? "s" : ""}</span>
            <button className="btn-ghost" style={{ marginLeft: "auto" }} onClick={reset}>
              <RefreshIcon size={12} /> New import
            </button>
          </div>

          <div className="rule-list">
            {Array.from(subResults.entries()).map(([sub, res]) => {
              const isSplit = res.xml_files.length > 1;
              return (
                <div key={sub} className="rule-row">
                  <span className={`rs ${res.total_errors > 0 ? "rs-err" : res.total_skipped > 0 ? "rs-wrn" : "rs-ok"}`}>
                    {res.total_errors > 0 ? "✕" : res.total_skipped > 0 ? "⚠" : "✓"}
                  </span>
                  <div className="ri">
                    <span className="ri-name">
                      {sub === "_root" ? "/ (root)" : `${sub}/`}
                    </span>
                    <span className="ri-meta">
                      {res.total_rules} rule{res.total_rules !== 1 ? "s" : ""}
                      {res.total_errors  > 0 && ` · ${res.total_errors} error(s)`}
                      {res.total_skipped > 0 && ` · ${res.total_skipped} skipped`}
                      {isSplit
                        ? ` · ${res.xml_files.length} split files → ${sub}_rules-1.xml…`
                        : ` · ${sub === "_root" ? "_root" : sub}_rules.xml`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <button className="btn-dl" onClick={downloadResult}>
            <DownloadIcon size={15} /> {dlName}{" "}
            <span style={{ opacity: 0.7, fontSize: 12, fontWeight: 400 }}>
              {subResults.size} file{subResults.size !== 1 ? "s" : ""} · {totalRules} rules
            </span>
          </button>
        </div>
      </div>
    );
  }

  // ── Pending: file validated, waiting for Convert ──────────────────────────
  if (pendingFile && !processing) {
    const isZip  = pendingType === "zip";
    const sizeFmt = pendingFile.size > 1048576
      ? `${(pendingFile.size / 1048576).toFixed(1)} MB`
      : `${(pendingFile.size / 1024).toFixed(1)} KB`;

    return (
      <div className="batch-area">
        <div className="batch-pending fade-in">
          <div className="bp-file-row">
            <span className="bp-file-icon">{isZip ? "⊞" : "⎘"}</span>
            <div className="bp-file-info">
              <span className="bp-file-name">{pendingFile.name}</span>
              <span className="bp-file-meta">{sizeFmt} · {isZip ? "ZIP archive" : "YAML"}</span>
            </div>
            <button className="btn-ghost" onClick={reset}><XIcon size={11} /> Clear</button>
          </div>
          <div className="bp-ready-row">
            <span className="bp-ready-check">✓</span>
            <div>
              <div className="bp-ready-title">File validated, ready to convert</div>
              <div className="bp-ready-sub">
                Press <strong>Convert</strong> in the toolbar, or use{" "}
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--accent)" }}>Ctrl+Enter</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Drop zone ─────────────────────────────────────────────────────────────
  return (
    <div className="batch-area">
      <input
        ref={fileRef}
        type="file"
        accept=".yml,.yaml,.zip"
        style={{ display: "none" }}
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
      />

      <div
        className={`dropzone${dragging ? " over" : ""}`}
        style={{ pointerEvents: processing ? "none" : "auto" }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
        }}
        onClick={() => fileRef.current?.click()}
      >
        <DottedSurface />
        <div className="dz-icon">
          {processing ? <SpinnerIcon size={24} /> : <UploadIcon size={24} />}
        </div>
        <h3 className="dz-title">
          {processing ? (progress || "Converting…") : "Drop a file here"}
        </h3>

        {!processing && (
          <>
            <p className="dz-sub">
              Single or multi-doc{" "}
              <span className="dz-code">.yml</span>
              {" "}or a{" "}
              <span className="dz-code">.zip</span>
              {" "}of YAML rules for batch import
            </p>
            <button
              className="btn-browse"
              onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
            >
              Browse
            </button>
          </>
        )}

        {error && (
          <p style={{ color: "#ef4444", fontSize: 12, marginTop: 8, maxWidth: 320 }}>
            ✕ {error}
          </p>
        )}
      </div>
    </div>
  );
}
